import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ImagePlus, Trash2 } from 'lucide-react';
import { createEvent, updateEvent, uploadEventCover, deleteEventCover, COVER_MAX_MB, EventRow } from '../api';
import { listOperators, Operator } from '../../users/api';
import { useToast } from '../../../context/ToastContext';

const labelCls = 'mb-1 block text-xs font-medium text-content-2';
const inputCls = 'w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-content outline-none focus:border-brand-blue';

/** datetime "YYYY-MM-DDTHH:mm:ss" → "YYYY-MM-DDTHH:mm" para el input datetime-local. */
function toInput(dt: string | null | undefined) {
  if (!dt) return '';
  const m = dt.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  return m ? `${m[1]}T${m[2]}` : '';
}

export default function EventForm({ event, onClose, onSaved }: {
  event?: EventRow | null; onClose: () => void; onSaved: (id?: number) => void;
}) {
  const toast = useToast();
  const editing = !!event;
  const [name, setName] = useState(event?.name || '');
  const [datetime, setDatetime] = useState(toInput(event?.datetime));
  const [price, setPrice] = useState(event?.price != null ? String(event.price) : '');
  const [capacity, setCapacity] = useState(event?.capacity != null ? String(event.capacity) : '');
  const [venue, setVenue] = useState(event?.venue || '');
  const [eventType, setEventType] = useState(event?.event_type || '');
  const [description, setDescription] = useState(event?.description || '');
  const [saving, setSaving] = useState(false);
  // Acceso por evento (28-oct-2026, Mabel: «todas deben tener acceso al festival de diciembre; los demás festivales solo
  // Karla y yo»): todo el equipo o solo una lista de personas (los dueños siempre); y si quien entra puede vender boletos.
  const [accesoModo, setAccesoModo] = useState<'todos' | 'lista'>(event?.access_user_ids?.length ? 'lista' : 'todos');
  const [accesoIds, setAccesoIds] = useState<Set<number>>(new Set(event?.access_user_ids || []));
  const [equipoVende, setEquipoVende] = useState(!!event?.equipo_vende);
  const [operadores, setOperadores] = useState<Operator[]>([]);
  useEffect(() => { listOperators().then((r) => setOperadores(r.users)).catch(() => {}); }, []);
  // Portada (28-oct-2026): el archivo elegido se sube DESPUÉS de guardar el evento (un evento nuevo no tiene id
  // hasta crearse). `coverFile` = nueva imagen pendiente · `removeCover` = quitar la actual al guardar.
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(event?.image_url || null);
  const [removeCover, setRemoveCover] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  useEffect(() => () => { if (coverPreview && coverPreview.startsWith('blob:')) URL.revokeObjectURL(coverPreview); }, [coverPreview]);

  function pickCover(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';   // permite volver a elegir el mismo archivo
    if (!f) return;
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (!(f.type.startsWith('image/') || ['jpg', 'jpeg', 'png', 'webp', 'heic'].includes(ext))) { toast.error('Elige una imagen (JPG, PNG o WebP)'); return; }
    if (f.size > COVER_MAX_MB * 1024 * 1024) { toast.error(`La imagen pesa más de ${COVER_MAX_MB} MB`); return; }
    setCoverFile(f); setRemoveCover(false); setCoverPreview(URL.createObjectURL(f));
  }
  function quitarCover() {
    setCoverFile(null); setCoverPreview(null);
    if (event?.image_url) setRemoveCover(true);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { toast.error('Ponle un nombre al evento'); return; }
    setSaving(true);
    let id = event?.event_id;
    try {
      const payload = {
        name: name.trim(), datetime: datetime || null,
        price: price === '' ? null : Number(price),
        capacity: capacity === '' ? null : Number(capacity),
        venue: venue || null, event_type: eventType || null, description: description || null,
        access_user_ids: accesoModo === 'lista' ? [...accesoIds] : null, equipo_vende: (equipoVende ? 1 : 0) as 0 | 1,
      };
      if (editing) await updateEvent(event!.event_id, payload);
      else id = (await createEvent(payload)).event_id;
    } catch (err: any) {
      toast.error(err?.message || 'No se pudo guardar');
      setSaving(false);
      return;
    }
    // La portada va aparte: si falla, el evento YA quedó guardado y se avisa solo de la imagen.
    try {
      if (coverFile && id) await uploadEventCover(id, coverFile);
      else if (removeCover && id) await deleteEventCover(id);
      toast.success(editing ? 'Evento actualizado' : 'Evento creado');
    } catch (err: any) {
      toast.error(`El evento se guardó, pero la portada no: ${err?.message || 'error al subirla'}`);
    } finally { setSaving(false); }
    onSaved(editing ? undefined : id);
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-content">{editing ? 'Editar evento' : 'Nuevo evento'}</h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content" aria-label="Cerrar"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className={labelCls}>Nombre *</label>
            <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej. Recital de fin de año" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Fecha y hora</label>
              <input type="datetime-local" className={inputCls} value={datetime} onChange={(e) => setDatetime(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Tipo</label>
              <input className={inputCls} value={eventType} onChange={(e) => setEventType(e.target.value)} placeholder="Cierre de cursos" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Precio del boleto (MXN)</label>
              <input type="number" min="0" step="1" className={inputCls} value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0" />
            </div>
            <div>
              <label className={labelCls}>Cupo</label>
              <input type="number" min="0" step="1" className={inputCls} value={capacity} onChange={(e) => setCapacity(e.target.value)} placeholder="—" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Lugar</label>
            <input className={inputCls} value={venue} onChange={(e) => setVenue(e.target.value)} placeholder="Teatro del Estado" />
          </div>
          <div>
            <label className={labelCls}>Descripción</label>
            <textarea className={`${inputCls} min-h-[80px]`} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          {/* Acceso (28-oct-2026): quién puede entrar a este evento y si el equipo puede vender boletos. */}
          <div data-qa="acceso" className="rounded-xl border border-line bg-surface-2/60 p-3">
            <label className={labelCls}>Quién puede entrar a este evento</label>
            <div className="flex flex-wrap gap-4 text-sm text-content">
              <label className="inline-flex items-center gap-1.5"><input type="radio" name="acceso" checked={accesoModo === 'todos'} onChange={() => setAccesoModo('todos')} /> Todo el equipo</label>
              <label className="inline-flex items-center gap-1.5"><input type="radio" name="acceso" checked={accesoModo === 'lista'} onChange={() => setAccesoModo('lista')} /> Solo estas personas</label>
            </div>
            {accesoModo === 'lista' && (
              <div className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-2" data-qa="acceso-lista">
                {operadores.filter((o) => o.is_active).map((o) => (
                  <label key={o.id} className="inline-flex items-center gap-1.5 text-sm text-content">
                    <input type="checkbox" disabled={o.role === 'owner'} checked={o.role === 'owner' || accesoIds.has(o.id)}
                      onChange={(e) => { const n = new Set(accesoIds); if (e.target.checked) n.add(o.id); else n.delete(o.id); setAccesoIds(n); }} />
                    <span>{o.name}</span>{o.role === 'owner' && <span className="text-[10px] text-content-3">dueño · siempre</span>}
                  </label>
                ))}
                {operadores.length === 0 && <span className="text-xs text-content-3">Cargando equipo…</span>}
              </div>
            )}
            <label className="mt-2 flex items-start gap-1.5 text-sm text-content">
              <input type="checkbox" className="mt-0.5" checked={equipoVende} onChange={(e) => setEquipoVende(e.target.checked)} />
              <span>Quien tiene acceso también puede <strong>vender y entregar boletos y abrir la caja</strong> del festival, aunque su rol solo pueda ver.</span>
            </label>
          </div>
          {/* Portada: se ve en la tarjeta de la lista, en la cabecera del evento y en el portal de alumnos. */}
          <div data-qa="portada">
            <label className={labelCls}>Portada del evento</label>
            <div className="overflow-hidden rounded-xl border border-line bg-surface-2">
              <div className="relative aspect-[16/9] w-full">
                {coverPreview ? (
                  <img src={coverPreview} alt="Portada del evento" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-content-3">
                    <ImagePlus size={22} />
                    <span className="text-xs">Sin portada: se usa el degradado de marca</span>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-3 py-2">
                <span className="text-[11px] text-content-3">JPG, PNG o WebP hasta {COVER_MAX_MB} MB · se optimiza sola (WebP, 1600 px)</span>
                <div className="flex gap-2">
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickCover} />
                  <button type="button" onClick={() => fileRef.current?.click()}
                    className="inline-flex items-center gap-1 rounded-lg border border-line bg-surface px-2.5 py-1.5 text-xs font-medium text-content-2 hover:bg-surface-2">
                    <ImagePlus size={13} /> {coverPreview ? 'Cambiar' : 'Subir imagen'}
                  </button>
                  {coverPreview && (
                    <button type="button" onClick={quitarCover}
                      className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-red-500 hover:bg-red-500/10">
                      <Trash2 size={13} /> Quitar
                    </button>
                  )}
                </div>
              </div>
            </div>
            {removeCover && <p className="mt-1 text-[11px] text-content-3">La portada se quitará al guardar.</p>}
            {coverFile && <p className="mt-1 text-[11px] text-content-3">Se subirá «{coverFile.name}» al guardar.</p>}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-content-2 hover:bg-surface-2">Cancelar</button>
            <button type="submit" disabled={saving}
              className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-on-accent disabled:opacity-60">
              {saving ? 'Guardando…' : editing ? 'Guardar' : 'Crear evento'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
